/**
 * SHIFT-ANNOUNCE-001 — mandatory, premium acknowledgment modal for shift-change announcements.
 * Queue (one at a time), no X / Esc / outside-click dismissal; the only exit is the acknowledge
 * button, which records announcement_id + user_id + acknowledged_at server-side. Pending items
 * reappear on every eligible page load until acknowledged. Other announcement kinds are untouched.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Megaphone, User, CalendarDays, Clock3, ArrowRight, MapPin, CalendarCheck, UserCog, Info, CheckCircle2, Loader2, Zap } from 'lucide-react';
import { formatDateTimeShortET, formatDateOnly } from '../../utils/formatters';
import './shift-announce.css';

const API = process.env.REACT_APP_BACKEND_URL;
const HERO = '/assets/shift-announcement-hero.png';
const longDate = (iso) => (iso ? formatDateOnly(iso) : '—');

const Row = ({ icon: Icon, label, value, testId }) => (
  <div className="sa-row" data-testid={testId}>
    <Icon className="sa-row-icon" aria-hidden="true" />
    <span className="sa-row-label">{label}</span>
    <span className="sa-row-value">{value}</span>
  </div>
);

export const ShiftChangeAcknowledgeModal = () => {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [safe, setSafe] = useState(true);
  const lastFetch = useRef(0);
  const location = useLocation();

  const fetchPending = useCallback(async (force = false) => {
    const token = localStorage.getItem('token');
    if (!token) { setQueue([]); return; }
    if (!force && Date.now() - lastFetch.current < 30000) return;
    lastFetch.current = Date.now();
    try {
      const res = await fetch(`${API}/api/it-dashboard/announcements/feed`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const pending = (data.announcements || []).filter((a) => a.requires_ack && (a.kind === 'shift_change' || a.kind === 'dst_change') && !a.is_acknowledged)
        .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
      setQueue(pending);
    } catch { /* best-effort; retried on next route change / interval */ }
  }, []);

  useEffect(() => { fetchPending(); }, [location.pathname, fetchPending]);
  useEffect(() => { const t = setInterval(() => fetchPending(true), 120000); return () => clearInterval(t); }, [fetchPending]);
  // Never interrupt another open dialog / in-progress save: wait for the next safe moment.
  useEffect(() => {
    if (!queue.length) return undefined;
    const check = () => setSafe(!document.querySelector('[role="dialog"]:not([data-shift-ack]), [data-saving="true"], [aria-busy="true"][data-form]'));
    check(); const t = setInterval(check, 1500); return () => clearInterval(t);
  }, [queue.length]);

  const current = queue[0];
  const acknowledge = async () => {
    if (!current || busy) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API}/api/it-dashboard/announcements/${current.id}/acknowledge`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setQueue((q) => q.filter((a) => a.id !== current.id));
    } catch (e) {
      setError('We could not record your acknowledgment. Please try again — the announcement stays pending until it is saved.');
    } finally { setBusy(false); }
  };

  if (!current || !safe) return null;
  const isDst = current.kind === 'dst_change';
  const sc = current.shift_change || {};
  const dc = current.dst_change || {};
  const block = (e) => e.preventDefault();
  if (isDst) {
    const ps = current.personal;
    const ended = !dc.dst_started;
    const tzLine = `${dc.timezone} · ${dc.previous_abbreviation} (${dc.previous_utc_offset}) → ${dc.new_abbreviation} (${dc.new_utc_offset})`;
    if (ps) {
      const Cmp = ({ label, prev, next, icon: Icon, testId }) => (
        <div className="sa-compare sa-compare-tight" data-testid={testId}>
          <div className="sa-shift sa-shift-prev"><Icon className="sa-shift-icon" aria-hidden="true" />
            <div><div className="sa-shift-label">Previous {label}</div><div className="sa-shift-time">{prev.label}</div></div></div>
          <ArrowRight className="sa-arrow" aria-hidden="true" />
          <div className="sa-shift sa-shift-new"><Icon className="sa-shift-icon" aria-hidden="true" />
            <div><div className="sa-shift-label">New {label}</div><div className="sa-shift-time">{next.label}</div></div></div>
        </div>
      );
      return (
        <AckShell block={block} title="Important Update: Your Shift Login Time Has Changed" titleTestId="dst-ack-title" modalTestId="dst-ack-modal"
          subtitle={`${dc.region} Daylight Saving Time ${ended ? 'ended' : 'started'} on ${longDate(dc.effective_date)}. Your ${ps.shift_timezone} shift hours stay the same, so your ${ps.reporting_region} reporting times move.`}
          error={error} queue={queue} busy={busy} onAck={acknowledge}>
          <div className="sa-employee" data-testid="dst-ack-shift"><Clock3 className="sa-row-icon" aria-hidden="true" /><span className="sa-row-label">Your shift</span>
            <span className="sa-employee-name">{ps.shift_label} {ps.shift_tz_new_abbr}</span></div>
          <Cmp label="Login" prev={ps.previous_login} next={ps.new_login} icon={CalendarDays} testId="dst-ack-login" />
          <Cmp label="Logout" prev={ps.previous_logout} next={ps.new_logout} icon={Clock3} testId="dst-ack-logout" />
          <div className="sa-action" data-testid="dst-ack-action"><Megaphone className="w-5 h-5" aria-hidden="true" />{ps.action}</div>
          <div className="sa-rows sa-rows-secondary">
            <Row icon={CalendarCheck} label="Effective Date" value={longDate(dc.effective_date)} testId="dst-ack-effective" />
            <Row icon={MapPin} label="Timezone" value={tzLine} testId="dst-ack-timezone" />
          </div>
        </AckShell>
      );
    }
    return (
      <AckShell block={block} title={`Important Update: Daylight Saving Time Has ${ended ? 'Ended' : 'Started'}`} titleTestId="dst-ack-title" modalTestId="dst-ack-modal"
        subtitle={`${dc.region} Daylight Saving Time ${ended ? 'ended' : 'started'} on ${longDate(dc.effective_date)}. ${dc.clock_text}`}
        error={error} queue={queue} busy={busy} onAck={acknowledge}>
        <div className="sa-action" data-testid="dst-ack-general">
          <Info className="w-5 h-5" aria-hidden="true" />Employees assigned to {dc.timezone} keep their configured local shift hours. If you work with that team, check your schedule — their reporting times in your local time have moved by {Math.abs(dc.clock_shift_minutes) / 60} hour.
        </div>
        <div className="sa-rows sa-rows-secondary">
          <Row icon={CalendarCheck} label="Effective Date" value={longDate(dc.effective_date)} testId="dst-ack-effective" />
          <Row icon={MapPin} label="Timezone" value={tzLine} testId="dst-ack-timezone" />
          <Row icon={UserCog} label="Attendance" value="TitleFlow applies the correct offset automatically" testId="dst-ack-utc" />
        </div>
      </AckShell>
    );
  }
  return (
    <AckShell block={block} title="Shift Schedule Updated" titleTestId="shift-ack-title" modalTestId="shift-ack-modal"
      subtitle="There is an update to an employee's shift schedule. Please review the details below and acknowledge."
      error={error} queue={queue} busy={busy} onAck={acknowledge}>
      <div className="sa-employee" data-testid="shift-ack-employee">
        <User className="sa-row-icon" aria-hidden="true" /><span className="sa-row-label">Employee</span>
        <span className="sa-employee-name">{sc.employee_name}</span>
      </div>
      <div className="sa-compare">
        <div className="sa-shift sa-shift-prev" data-testid="shift-ack-previous">
          <CalendarDays className="sa-shift-icon" aria-hidden="true" />
          <div><div className="sa-shift-label">Previous Shift</div><div className="sa-shift-time">{sc.previous_shift}</div></div>
        </div>
        <ArrowRight className="sa-arrow" aria-hidden="true" />
        <div className="sa-shift sa-shift-new" data-testid="shift-ack-new">
          <Clock3 className="sa-shift-icon" aria-hidden="true" />
          <div><div className="sa-shift-label">New Shift</div><div className="sa-shift-time">{sc.new_shift}</div>
            {sc.is_future && <div className="sa-shift-note">Upcoming change</div>}</div>
        </div>
      </div>
      <div className="sa-rows">
        <Row icon={MapPin} label="Attendance Timezone" value={`${sc.timezone} (${sc.timezone_abbreviation}${sc.utc_offset ? `, ${sc.utc_offset}` : ''})`} testId="shift-ack-timezone" />
        <Row icon={CalendarCheck} label="Effective From" value={longDate(sc.effective_from)} testId="shift-ack-effective" />
        <Row icon={UserCog} label="Updated By" value={sc.updated_by} testId="shift-ack-updated-by" />
        <Row icon={Clock3} label="Updated On" value={formatDateTimeShortET(sc.updated_at || current.created_at)} testId="shift-ack-updated-on" />
      </div>
      <div className="sa-reminder" data-testid="shift-ack-reminder"><Info className="w-4 h-4" aria-hidden="true" />Please take note of the revised working hours and plan accordingly.</div>
    </AckShell>
  );
};

/* Shared premium shell: gradient header, mascot, white card (children), mandatory CTA. No close affordance. */
const AckShell = ({ block, title, subtitle, titleTestId, modalTestId, error, queue, busy, onAck, children }) => (
  <DialogPrimitive.Root open modal>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="sa-overlay" data-testid="shift-ack-overlay" />
      <DialogPrimitive.Content className="sa-modal" data-shift-ack="true" data-testid={modalTestId} aria-describedby="sa-desc"
        onEscapeKeyDown={block} onPointerDownOutside={block} onInteractOutside={block}>
        <div className="sa-brand"><span className="sa-brand-mark"><Zap className="w-4 h-4" /></span>TitleFlow HR</div>
        <div className="sa-head">
          <span className="sa-pill" data-testid="shift-ack-pill"><Megaphone className="w-4 h-4" /> Important Announcement</span>
          <DialogPrimitive.Title className="sa-title" data-testid={titleTestId}>{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description id="sa-desc" className="sa-subtitle">{subtitle}</DialogPrimitive.Description>
        </div>
        <div className="sa-body">
          <div className="sa-hero" aria-hidden="true"><img src={HERO} alt="" className="sa-hero-img" /></div>
          <div className="sa-card" data-testid="shift-ack-card">{children}</div>
        </div>
        {error && <p className="sa-error" role="alert" data-testid="shift-ack-error">{error}</p>}
        <div className="sa-foot">
          {queue.length > 1 && <span className="sa-queue" data-testid="shift-ack-queue">{queue.length - 1} more announcement{queue.length > 2 ? 's' : ''} after this</span>}
          <button type="button" className="sa-btn" onClick={onAck} disabled={busy} autoFocus data-testid="shift-ack-button">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />} I Have Read &amp; Acknowledge
          </button>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);

export default ShiftChangeAcknowledgeModal;
